import axios from "axios";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EyeSlashed, Mail, Pass } from "../../images/icons/JsxIcons";
import { NotificationService } from "../../services/Notification.service";
import { encryptData } from "../../services/encryption.service";
import { useStoreAuthV2 } from "../../store/AuthV2Store";
import { useStoreUserManager } from "../../store/UserManage";
import { useStorePageManager } from "../../store/PageManage";

export const AuthV2LoginPanel = ({
  currentState = 1,
  panelState,
  setPanelState,
}) => {
  const { t } = useTranslation();
  const { identifier, resetAuthV2State } = useStoreAuthV2();
  const handleUpdateUser = useStoreUserManager(
    (state) => state.handleUpdateUser,
  );
  const handleUpdateBlockScreen = useStorePageManager(
    (state) => state.handleUpdateBlockScreen,
  );

  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [seePassword, setSeePassword] = useState(false);
  const [statePassword, setStatePassword] = useState({
    focus: false,
    error: false,
    good: false,
  });

  useEffect(() => {
    if (password.length > 0) {
      if (password.length >= 8) {
        setStatePassword({ ...statePassword, error: false, good: true });
      } else {
        setStatePassword({ ...statePassword, error: true, good: false });
      }
    } else {
      setStatePassword({ ...statePassword, error: false, good: false });
    }
  }, [password]);

  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return email;
    const [localPart, domain] = email.split("@");
    if (localPart.length <= 2) return email;
    return `${localPart[0]}${"*".repeat(Math.min(3, localPart.length - 1))}@${domain}`;
  };

  const handleLogin = async () => {
    if (!statePassword.good || loading) return;

    setLoading(true);
    handleUpdateBlockScreen(true);

    try {
      const res = await axios.post(
        process.env.REACT_APP_API_URL + "/users/login",
        {
          email: identifier,
          password: password,
        },
      );

      // Check profile restrictions
      if (
        res.data.profile.toString().toLowerCase() !==
        "client".toString().toLowerCase()
      ) {
        NotificationService.addToQueue(
          1,
          t("modals.connexionPanel.notification.blockAccount"),
        );
        handleUpdateBlockScreen(false);
        setLoading(false);
        return;
      }

      if (!res.data.enableClient) {
        NotificationService.addToQueue(
          1,
          t("modals.connexionPanel.notification.blockClient"),
        );
        handleUpdateBlockScreen(false);
        setLoading(false);
        return;
      }

      if (!res.data.payment_method_id && res.data.enableHost) {
        NotificationService.addToQueue(
          1,
          t("modals.connexionPanel.notification.paymentMethod"),
        );
      }

      NotificationService.addToQueue(
        0,
        t("modals.connexionPanel.authV2.loginSuccess"),
      );

      // Save tokens
      const dataToken = {
        access_token: res.data.access_token,
        refresh_token: res.data.refresh_token,
      };
      const encryptToken = await encryptData(dataToken);
      localStorage.setItem("token", encryptToken);

      const dataUserSave = {
        token: res.data.token,
        email: res.data.email,
      };
      const dataEncrypted = await encryptData(dataUserSave);
      localStorage.setItem("user", dataEncrypted);

      // Update user state
      const detectedSocialType =
        res.data.socialType ||
        (res.data.facebookEmail
          ? "facebook"
          : res.data.googleEmail
            ? "google"
            : res.data.appleEmail
              ? "apple"
              : "");
      handleUpdateUser({
        id: res.data.id,
        lastname: res.data.lastname,
        firstname: res.data.firstname,
        location: res.data.location,
        connected: true,
        profil: res.data.profile + `${res.data.enableHost ? "/hote" : ""}`,
        mail: res.data.email,
        contact: res.data.contact,
        emailVerified: res.data.mailActivatedAt && true,
        identityDoc: res.data.identityDoc ?? null,
        pieceChecked: res.data.identityDoc ? 1 : 0,
        profilImgUrl: res.data.avatar ?? null,
        contactChecked: res.data.contactActivatedAt && true,
        primaryIdentifierType:
          res.data.contactActivatedAt && !res.data.mailActivatedAt
            ? "PHONE"
            : "EMAIL",
        isSocial: Boolean(detectedSocialType),
        typeSocial: detectedSocialType,
        enableHost: res.data.enableHost,
        payment_method_id: res.data.payment_method_id ?? 0,
      });

      // Close panel and reset state
      resetAuthV2State();
      useStorePageManager.getState().handleUpdateConnexionPanelState(false);
      handleUpdateBlockScreen(false);
    } catch (error) {
      handleUpdateBlockScreen(false);
      setLoading(false);

      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        t("modals.connexionPanel.authV2.loginError");

      NotificationService.addToQueue(2, errorMsg);
    }
  };

  const handleBack = () => {
    setPassword("");
    setPanelState({
      ...panelState,
      authV2Login: 0,
      authV2Entry: 1,
    });
  };

  const handleForgotPassword = () => {
    setPanelState({
      ...panelState,
      authV2Login: 0,
      passwordForgotten: 1,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && statePassword.good && !loading) {
      handleLogin();
    }
  };

  return (
    <div
      className={`contains inscription-part ${
        currentState === 0
          ? "go-left-side"
          : currentState === 1
            ? "come-center"
            : "go-right-side"
      } | flcolm`}
    >
      <button className="back-btn | btn" onClick={handleBack}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M13 1L1 13M13 13L1 1"
            stroke="var(--gray-900)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <span className="auth-v2-panel-title">
        {t("modals.connexionPanel.authV2.loginTitle")}
      </span>

      <label className="auth-v2-welcome">
        {t("modals.connexionPanel.authV2.loginSubtitle")}
      </label>

      <div className="auth-v2-masked-email | flrow allcenter">
        <Mail color="var(--gray-500)" />
        <span>{maskEmail(identifier)}</span>
      </div>

      <div className="input-field | flcolm">
        <div className="input | rlve">
          <Pass color={statePassword.focus ? "var(--gray-600)" : "none"} />
          <input
            onBlur={() => setStatePassword({ ...statePassword, focus: false })}
            onFocus={() => setStatePassword({ ...statePassword, focus: true })}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            value={password}
            placeholder={t("modals.connexionPanel.authV2.passwordPlaceholder")}
            type={seePassword ? "text" : "password"}
            autoFocus
          />
          <button
            className="see-pass | btn"
            onClick={() => setSeePassword(!seePassword)}
          >
            <EyeSlashed
              color={seePassword ? "var(--gray-600)" : "var(--gray-400)"}
            />
          </button>
          {!statePassword.focus && statePassword.error && (
            <div className="detect-issue-input">
              <div className="chevron"></div>
              <span>{t("modals.connexionPanel.authV2.passwordTooShort")}</span>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={handleLogin}
        className="next-btn | btn flex"
        disabled={!statePassword.good || loading}
      >
        {loading
          ? t("modals.connexionPanel.authV2.loading")
          : t("modals.connexionPanel.authV2.loginButton")}
      </button>

      <button
        onClick={handleForgotPassword}
        className="forgot-password-link | btn"
      >
        {t("modals.connexionPanel.authV2.forgotPassword")}
      </button>
    </div>
  );
};
